// ============================================================
// スポットライト1行の組み立て (G2 text container 用)
// ============================================================
// LVGL フォントは単一・固定サイズなので、文字の "意味" を明示することで
// パッと見の可読性を上げる。記号だけだと初見では分からない。

import type { SpotlightItem } from '../types'
import { bearingToCompassJa } from '../util/distance'

/**
 * 例:
 *   "次のバス  050系統"
 *   "          189m 南東   3分"
 */
export function composeSpotlight(item: SpotlightItem | null): string {
  if (!item) return ' '
  const label = item.kind === 'bus' ? '次のバス' : '次の電車'
  const distStr = formatDistance(item.distanceM)
  const compass = bearingToCompassJa(item.bearing)
  const arrow = item.destination ? ` → ${item.destination}` : ''
  // ETA が不明 (Aggregator の sentinel >= 90) の場合は表示を省略 (距離だけ)
  const eta = item.mins >= 90 ? '' : `   ${item.mins}分`
  return `${label}  ${item.line}${arrow}\n          ${distStr} ${compass}${eta}`
}

/**
 * 例:
 *   "最寄駅 東京 223m 西"
 *   "バス停 15件 < 200m"
 */
export function composeSummary(opts: {
  nearestStation?: { name: string; distanceM: number; bearing: number }
  busstopCount: number
  busstopRadiusM: number
}): string {
  const lines: string[] = []
  if (opts.nearestStation) {
    const dist = formatDistance(opts.nearestStation.distanceM)
    const c = bearingToCompassJa(opts.nearestStation.bearing)
    lines.push(`最寄駅  ${opts.nearestStation.name}  ${dist} ${c}`)
  }
  if (opts.busstopCount > 0) {
    lines.push(`バス停  ${opts.busstopCount}件 < ${opts.busstopRadiusM}m`)
  }
  return lines.length === 0 ? ' ' : lines.join('\n')
}

export function composeHeader(opts: { radiusM: number; headingDeg: number | null }): string {
  // "表示範囲 500m" — 何の数値か分かるラベルにする
  const r = `表示${opts.radiusM}m`
  const heading = opts.headingDeg == null ? '↑北' : `${Math.round(opts.headingDeg)}°`
  return `${r}   ${heading}`
}

export function composeTime(date: Date): string {
  return formatTime(date)
}

/** Sonar の凡例。LVGL フォントに収録されている記号のみを使う。 */
export function composeLegend(): string {
  // U+25A1 □ (LVGL OK), U+25CF ● (OK), U+25B2 ▲ (OK), 中黒 ・ (OK)
  return '●バス  □駅  ▲電車  ・停'
}

/** 画面下端の操作ヒント */
export function composeHint(): string {
  return 'タップ:半径切替   ダブルタップ:詳細'
}

/**
 * 左カラム全部を 1 つの text にまとめる。
 * LVGL の native 改行に任せると container 境界での切れが起きない。
 */
export function composeLeftPanel(opts: {
  date: Date
  radiusM: number
  headingDeg: number | null
  spotlightBus: SpotlightItem | null
  spotlightTrain: SpotlightItem | null
  summary: {
    nearestStation?: { name: string; distanceM: number; bearing: number }
    busstopCount: number
    busstopRadiusM: number
  }
  /** 運行情報: undefined = 未取得 / null = 平常運転 / string = アラート本文 */
  alertText?: string | null
}): string {
  const time = formatTime(opts.date)
  const header = composeHeader({ radiusM: opts.radiusM, headingDeg: opts.headingDeg })
  const lines: string[] = []
  // 1 行目: 時刻 + 表示範囲 + 方位
  lines.push(`${time}    ${header}`)
  lines.push('')

  // バス
  if (opts.spotlightBus) {
    lines.push(composeSpotlight(opts.spotlightBus))
  }
  // 電車
  if (opts.spotlightTrain) {
    lines.push('')
    lines.push(composeSpotlight(opts.spotlightTrain))
  }
  lines.push('')

  // サマリ (最寄駅 + バス停)
  const sum = composeSummary(opts.summary)
  if (sum.trim().length > 0) lines.push(sum)

  // 運行情報 (1 行)
  // ⚠ (U+26A0) は LVGL の収録フォントに無いので使わない (実機で謎のプレースホルダになる)
  if (opts.alertText !== undefined) {
    lines.push('')
    if (opts.alertText === null) {
      lines.push('運行情報  平常運転')
    } else {
      // 長文は 28 文字程度で切る (LVGL の改行に任せたいが container 幅と相談)
      const t = opts.alertText.length > 56 ? opts.alertText.slice(0, 55) + '…' : opts.alertText
      lines.push(`運行情報 異常  ${t}`)
    }
  }

  return lines.join('\n')
}

// ───────── format helpers ─────────

function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)}m`
  return `${(m / 1000).toFixed(1)}km`
}

function formatTime(d: Date): string {
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${hh}:${mm}`
}
